import React, { useState, useEffect } from 'react';
import {
  Box,
  Fab,
  Badge,
  Drawer,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { aiApi } from '../../api/aiApi';
import { AICampaignRecommendation, AIAction, HistoryDayGroup } from '../../@types/ai';
import { useAuthContext } from '../../auth/useAuthContext';
import { OneAdvertiserT } from '../../@types/advertiser';
import Iconify from '../iconify';

export default function AIFabManager() {
  const { user } = useAuthContext();
  const showAdmin = user?.role === 'sysAdmin';

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [pendingRecs, setPendingRecs] = useState<AICampaignRecommendation[]>([]);
  const [historyData, setHistoryData] = useState<HistoryDayGroup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  // Admin State
  const [advertisers, setAdvertisers] = useState<OneAdvertiserT[]>([]);
  const [selectedAdvId, setSelectedAdvId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [adminRecs, setAdminRecs] = useState<HistoryDayGroup[]>([]);
  const [isAdminLoading, setIsAdminLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const [expanded, setExpanded] = useState('');

  // Initial Fetch
  useEffect(() => {
    if (showAdmin) {
      const fetchAdvertisers = async () => {
        try {
          const data = await aiApi.getAdvertisers();
          setAdvertisers(data || []);
        } catch (error) {
          console.error('Failed to fetch advertisers:', error);
        }
      };
      fetchAdvertisers();
    }

    if (!showAdmin) {
      const fetchPending = async () => {
        try {
          setIsLoading(true);
          const response = await aiApi.getPending();
          if (response?.recommendations) setPendingRecs(response?.recommendations);
        } catch (error) {
          console.error('Failed to fetch pending recommendations:', error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchPending();
    }
  }, [showAdmin]);

  // Admin Fetch
  useEffect(() => {
    if (showAdmin && selectedAdvId) {
      const fetchAdminRecs = async () => {
        try {
          setIsAdminLoading(true);
          const data = await aiApi.getAdminRecommendations(
            selectedAdvId,
            selectedDate || undefined
          );
          if (data?.data) setAdminRecs(data.data);
          else setAdminRecs([]);
        } catch (error) {
          console.error('Failed to fetch admin recommendations:', error);
        } finally {
          setIsAdminLoading(false);
        }
      };
      fetchAdminRecs();
    }
  }, [showAdmin, selectedAdvId, selectedDate]);

  // Calculate total unread actions across all campaigns
  const unreadCount = showAdmin
    ? 0
    : pendingRecs.reduce((total, camp) => {
        console.log(total);
        return total + camp.actions.filter((a) => !a.readAt).length;
      }, 0);

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);

    if (!showAdmin) {
      const markReads: Promise<any>[] = [];

      const updatedRecs = pendingRecs.map((camp) => {
        const updatedActions = camp.actions.map((action) => {
          if (!action.readAt) {
            markReads.push(aiApi.markAsRead(action._id));
            return { ...action, readAt: new Date().toISOString() };
          }
          return action;
        });
        return { ...camp, actions: updatedActions };
      });

      Promise.all(markReads).catch(console.error);
      setPendingRecs(updatedRecs);
    }
  };

  const removeActionFromState = (campaignId: string, actionId: string) => {
    setPendingRecs((prev) =>
      prev
        .map((camp) => {
          if (camp.campaignId === campaignId) {
            return { ...camp, actions: camp.actions.filter((a) => a._id !== actionId) };
          }
          return camp;
        })
        .filter((camp) => camp.actions.length > 0)
    ); // Remove empty campaigns
  };

  const handleDismiss = async (campaignId: string, actionId: string) => {
    removeActionFromState(campaignId, actionId);
    try {
      await aiApi.dismissAction(actionId);
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  const handleApply = async (campaignId: string, actionId: string) => {
    removeActionFromState(campaignId, actionId);
    try {
      await aiApi.applyAction(actionId);
    } catch (error) {
      console.error('Failed to apply:', error);
    }
  };

  const handleLoadHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const data = await aiApi.getHistory();
      if (data?.data) setHistoryData(data.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleForceGenerate = async () => {
    if (!selectedAdvId) return;
    try {
      setIsGenerating(true);
      await aiApi.generateAdminRecommendations(selectedAdvId);
      const data = await aiApi.getAdminRecommendations(selectedAdvId, selectedDate || undefined);
      if (data?.data) setAdminRecs(data.data);
    } catch (error) {
      console.error('Failed to generate recommendations:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // --- RENDERING HELPERS ---

  const renderActionItem = (
    campaignId: string,
    action: AIAction,
    isHistory: boolean,
    isLast: boolean
  ) => {
    const hasTargetingChange = Boolean(action.targetingChange);
    const isDestructive = ['pause', 'decrease_budget', 'investigate_fraud'].includes(
      action.suggestedAction
    );

    return (
      <Box
        key={action._id}
        sx={{
          mb: isLast ? 0 : 2,
          pb: isLast ? 0 : 2,
          borderBottom: isLast ? 'none' : '1px solid #eee',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {action.insight}
        </Typography>

        {action.estimatedImpact && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
          >
            <TrendingUpIcon fontSize="inherit" color="success" />
            {action.estimatedImpact}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
          <Chip
            sx={{
              px: 1,
              textTransform: 'uppercase',
              fontWeight: 'bold',
              fontSize: '10px',
              color: 'text.secondary',
            }}
            label={action.suggestedAction.replace(/_/g, ' ')}
            size="small"
            variant="outlined"
          />
          {action.confidence && (
            <Chip
              label={`Confidence: ${Math.round(action.confidence * 100)}%`}
              sx={{
                px: 1,
                textTransform: 'uppercase',
                fontSize: '10px',
              }}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {/* !isHistory && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button
              size="small"
              color="inherit"
              onClick={() => handleDismiss(campaignId, action._id)}
            >
              Dismiss
            </Button>
            {hasTargetingChange && (
              <Button
                size="small"
                variant="contained"
                color={isDestructive ? 'warning' : 'primary'}
                onClick={() => handleApply(campaignId, action._id)}
                disableElevation
              >
                Apply
              </Button>
            )}
          </Box>
        ) */}
      </Box>
    );
  };

  const renderCampaignCard = (camp: AICampaignRecommendation, isHistory: boolean = false) => {
    console.log(camp, isHistory);
    const campKey = `${camp.campaignId}-${camp.country}`;
    const isExpanded = expanded === campKey;
    return (
      <Card key={campKey} sx={{ mb: 2, boxShadow: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <Typography
            variant="subtitle2"
            color="primary"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
          >
            <AutoAwesomeIcon fontSize="small" />
            {camp.campaignName || camp.campaignId} {camp.country ? `(${camp.country})` : ''}
          </Typography>

          <Box
            onClick={() => setExpanded(isExpanded ? '' : campKey)}
            sx={{
              position: 'absolute',
              left: 'calc(50% - 12px)',
              bottom: 0,
              cursor: 'pointer',
              '&:hover': {
                color: 'info.main',
              },
            }}
          >
            <Iconify
              icon={isExpanded ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
              width={24}
            />
          </Box>

          {isExpanded && (
            <Box>
              {camp.actions.map((action, index) =>
                renderActionItem(
                  camp.campaignId,
                  action,
                  isHistory,
                  index === camp.actions.length - 1
                )
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderAdminView = () => {
    const selectedAdvertiser = advertisers.find((adv) => adv._id === selectedAdvId) || null;

    return (
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Autocomplete
            options={advertisers}
            getOptionLabel={(option) => `${option.name} (${option._id})`}
            value={selectedAdvertiser}
            onChange={(_, newValue) => setSelectedAdvId(newValue ? newValue._id : '')}
            renderInput={(params) => (
              <TextField {...params} label="Search Advertiser" size="small" />
            )}
            fullWidth
            size="small"
            disablePortal={false}
          />

          <TextField
            label="Filter Date (Optional)"
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            disabled={!selectedAdvId || isGenerating}
            onClick={handleForceGenerate}
          >
            {isGenerating && 'Generating...'}
            {!isGenerating && 'Force Regenerate'}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isAdminLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isAdminLoading && adminRecs.length === 0 && selectedAdvId && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No recommendations found for this criteria.
          </Typography>
        )}

        {!isAdminLoading &&
          adminRecs.map((dayGroup) => (
            <Box key={dayGroup.date} sx={{ mb: 3 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
              >
                {dayGroup.date}
              </Typography>
              {dayGroup.recommendations.map((camp) => renderCampaignCard(camp, true))}
            </Box>
          ))}
      </Box>
    );
  };

  const renderUserView = () => {
    console.log('pendingRecs', pendingRecs);
    return (
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading &&
          pendingRecs.length > 0 &&
          pendingRecs.map((camp) => renderCampaignCard(camp, false))}

        {!isLoading && pendingRecs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', my: 4 }}>
            No new recommendations right now. You&#39;re all caught up!
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        {historyData.length === 0 && (
          <Button
            fullWidth
            variant="outlined"
            onClick={handleLoadHistory}
            disabled={isLoadingHistory}
          >
            {isLoadingHistory && 'Loading...'}
            {!isLoadingHistory && 'Show History'}
          </Button>
        )}

        {historyData.length > 0 && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Previous Insights (Last 7 Days)
            </Typography>
            {historyData.map((dayGroup) => (
              <Box key={dayGroup.date} sx={{ mb: 3 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}
                >
                  {dayGroup.date}
                </Typography>
                {dayGroup.recommendations.map((camp) => renderCampaignCard(camp, true))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Box>
        <Badge
          sx={{ '& > span': { zIndex: 1050 } }}
          badgeContent={unreadCount}
          color="info"
          overlap="circular"
        >
          <Fab size="small" color="primary" onClick={handleOpenDrawer}>
            <AutoAwesomeIcon />
          </Fab>
        </Badge>
      </Box>

      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, p: 2, backgroundColor: '#f8f9fa' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {showAdmin ? 'Admin: AI Insights' : 'AI Campaign Insights'}
          </Typography>
          <IconButton onClick={() => setIsDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {showAdmin && renderAdminView()}
        {!showAdmin && renderUserView()}
      </Drawer>
    </>
  );
}
